import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Users, ArrowRightCircle, RefreshCw, Calendar, Award, Target, Briefcase, Filter, Search } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ReportingCarriere = () => {
  const { user } = useAuth();
  const { filialeId, currentFiliale } = useFiliale();
  const [stats, setStats] = useState({
    total_mouvements: 0,
    total_plans: 0,
    total_entretiens: 0,
    total_demandes: 0,
    promotions_annee: 0,
    taux_promotion: 0,
    taux_mobilité: 0,
    ancienneté_moyenne: 0,
    mouvements_par_type: [],
    plans_par_statut: [],
    demandes_par_statut: [],
    entretiens_par_type: []
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    annee: new Date().getFullYear()
  });

  // Couleurs pour les graphiques
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

  useEffect(() => {
    fetchStats();
  }, [filialeId, filters]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = {};
      
      console.log('ReportingCarriere - User role:', user?.role);
      console.log('ReportingCarriere - Filiale ID from context:', filialeId);
      console.log('ReportingCarriere - Current filiale:', currentFiliale);
      
      // Utiliser le filiale_id du contexte
      // Pour super-admin avec filiale sélectionnée, utiliser cette filiale
      // Pour super-admin sans filiale, envoyer "all" pour voir toutes les filiales
      // Pour admin et RH, envoyer leur filiale_id
      if (user && user.role === 'super_admin') {
        if (filialeId) {
          params.filiale_id = filialeId;
          console.log('ReportingCarriere - Super-admin: using selected filiale:', filialeId);
        } else {
          params.filiale_id = 'all';
          console.log('ReportingCarriere - Super-admin: using all filiales');
        }
      } else if (filialeId) {
        params.filiale_id = filialeId;
        console.log('ReportingCarriere - Using filiale_id:', filialeId);
      }
      
      if (filters.annee) {
        params.annee = filters.annee;
        console.log('ReportingCarriere - Year:', filters.annee);
      }
      
      const queryString = new URLSearchParams(params).toString();
      console.log('ReportingCarriere - API call:', `/api/reporting-carriere${queryString ? '?' + queryString : ''}`);
      
      const response = await http.get(`/api/reporting-carriere${queryString ? '?' + queryString : ''}`);
      console.log('ReportingCarriere - API response:', response.data);
      
      setStats(response.data || {
        total_mouvements: 0,
        total_plans: 0,
        total_entretiens: 0,
        total_demandes: 0,
        promotions_annee: 0,
        taux_promotion: 0,
        taux_mobilité: 0,
        ancienneté_moyenne: 0,
        mouvements_par_type: [],
        plans_par_statut: [],
        demandes_par_statut: [],
        entretiens_par_type: []
      });
    } catch (error) {
      console.error('ReportingCarriere - Erreur chargement stats:', error);
      // Stats par défaut si l'API n'existe pas encore
      setStats({
        total_mouvements: 0,
        total_plans: 0,
        total_entretiens: 0,
        total_demandes: 0,
        promotions_annee: 0,
        taux_promotion: 0,
        taux_mobilité: 0,
        ancienneté_moyenne: 0,
        mouvements_par_type: [],
        plans_par_statut: [],
        demandes_par_statut: [],
        entretiens_par_type: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFiliales = async () => {
    try {
      const response = await http.get('/api/filiales');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Erreur chargement filiales:', error);
      return [];
    }
  };

  const kpiCards = [
    {
      title: 'Total Mouvements',
      value: stats.total_mouvements,
      icon: ArrowRightCircle,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'Plans de Carrière',
      value: stats.total_plans,
      icon: Target,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      title: 'Entretiens',
      value: stats.total_entretiens,
      icon: Calendar,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: 'Demandes Mobilité',
      value: stats.total_demandes,
      icon: Briefcase,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    }
  ];

  const kpiStats = [
    {
      title: 'Promotions cette année',
      value: stats.promotions_annee,
      icon: Award,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    },
    {
      title: 'Taux de promotion',
      value: `${stats.taux_promotion}%`,
      icon: TrendingUp,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600'
    },
    {
      title: 'Taux de mobilité',
      value: `${stats.taux_mobilité}%`,
      icon: ArrowRightCircle,
      color: 'bg-cyan-500',
      bgColor: 'bg-cyan-50',
      textColor: 'text-cyan-600'
    },
    {
      title: 'Ancienneté moyenne',
      value: `${stats.ancienneté_moyenne} ans`,
      icon: Users,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Reporting Carrière"
        description="Tableau de bord et statistiques sur la carrière"
        icon={BarChart3}
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchStats,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <h3 className="font-semibold text-gray-700">Filtres</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
              <select
                value={filters.annee}
                onChange={(e) => setFilters({ ...filters, annee: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {[2024, 2025, 2026, 2027].map(annee => (
                  <option key={annee} value={annee}>{annee}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-gray-500">
            Chargement des statistiques...
          </div>
        ) : (
          <>
            {/* KPI Cards - Totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {kpiCards.map((kpi, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{kpi.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                      <kpi.icon className={`h-6 w-6 ${kpi.textColor}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* KPI Cards - Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {kpiStats.map((kpi, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{kpi.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                      <kpi.icon className={`h-6 w-6 ${kpi.textColor}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Sections supplémentaires avec graphiques */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Mouvements par type - Bar Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition des mouvements</h3>
                <div className="h-64">
                  {stats.mouvements_par_type && stats.mouvements_par_type.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.mouvements_par_type}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-gray-500">
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              </div>

              {/* Plans par statut - Pie Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Plans de carrière par statut</h3>
                <div className="h-64">
                  {stats.plans_par_statut && stats.plans_par_statut.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.plans_par_statut}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.statut}: ${entry.count}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {stats.plans_par_statut.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-gray-500">
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              </div>

              {/* Demandes par statut - Pie Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Demandes de mobilité par statut</h3>
                <div className="h-64">
                  {stats.demandes_par_statut && stats.demandes_par_statut.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.demandes_par_statut}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.statut}: ${entry.count}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {stats.demandes_par_statut.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-gray-500">
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              </div>

              {/* Entretiens par type - Bar Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Entretiens par type</h3>
                <div className="h-64">
                  {stats.entretiens_par_type && stats.entretiens_par_type.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.entretiens_par_type}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type_entretien" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10b981" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-gray-500">
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Note informationnelle */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Les statistiques affichées sont basées sur les données disponibles. 
                Pour un reporting avancé avec des graphiques détaillés et l'export PDF/Excel, 
                un développement supplémentaire est requis.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportingCarriere;
